import React, { useState, useEffect, useRef, useCallback } from 'react';
import { CheckCircle2, Circle, Shield, Activity } from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';
import {
  SimScreen,
  ProjectSetup,
  Message,
  Manifest,
  ConstraintConfig,
  AIScenarioConfig,
  SimResults,
  DEFAULT_CONSTRAINTS,
} from './datasim.types';
import DataSimSetup from './screens/DataSimSetup';
import DataSimIntake from './screens/DataSimIntake';
import DataSimConstraintReview from './screens/DataSimConstraintReview';
import DataSimScenarioBuilder from './screens/DataSimScenarioBuilder';
import DataSimResults from './screens/DataSimResults';

interface DataSimProps {
  loadSessionId?: number | null;
  onSessionChange?: (sessionId: number | null) => void;
}

// ─── Step definitions ──────────────────────────────────────────────────────────

const STEPS: { id: SimScreen; label: string }[] = [
  { id: 'setup', label: 'Setup' },
  { id: 'intake', label: 'Interview' },
  { id: 'constraints', label: 'Constraints' },
  { id: 'scenarios', label: 'AI Scenarios' },
  { id: 'results', label: 'Results' },
];

// ─── Wizard ────────────────────────────────────────────────────────────────────

export default function DataSimRiskAnalyzer({ loadSessionId, onSessionChange }: DataSimProps) {
  const [screen, setScreen] = useState<SimScreen>('setup');

  // Data state shared across screens
  const [setup, setSetup] = useState<ProjectSetup | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [constraintConfig, setConstraintConfig] = useState<ConstraintConfig>(DEFAULT_CONSTRAINTS);
  const [aiConfig, setAiConfig] = useState<AIScenarioConfig | null>(null);
  const [simResults, setSimResults] = useState<SimResults | null>(null);
  const [simulating, setSimulating] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [maxStep, setMaxStep] = useState(0);
  const [sessionId, setSessionId] = useState<number | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Session save (debounced) ─────────────────────────────────────────────

  const saveSession = useCallback(async (
    overrides?: { screen?: SimScreen; setup?: ProjectSetup | null; manifest?: Manifest | null;
      messages?: Message[]; constraintConfig?: ConstraintConfig; aiConfig?: AIScenarioConfig | null;
      simResults?: SimResults | null }
  ) => {
    const s = overrides?.setup ?? setup;
    if (!s) return; // nothing to save until setup is done
    try {
      const res = await api.post('/api/datasim/sessions', {
        sessionId,
        setup: s,
        messages: overrides?.messages ?? messages,
        manifest: overrides?.manifest ?? manifest,
        constraintConfig: overrides?.constraintConfig ?? constraintConfig,
        aiConfig: overrides?.aiConfig ?? aiConfig,
        simResults: overrides?.simResults ?? simResults,
        currentScreen: overrides?.screen ?? screen,
      });
      const newId = res.data.sessionId;
      if (newId && newId !== sessionId) {
        setSessionId(newId);
        onSessionChange?.(newId);
      }
    } catch {
      // silent — don't block UX for save failures
    }
  }, [sessionId, setup, messages, manifest, constraintConfig, aiConfig, simResults, screen, onSessionChange]);

  const debouncedSave = useCallback((overrides?: Parameters<typeof saveSession>[0]) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveSession(overrides), 1500);
  }, [saveSession]);


  const flushSave = useCallback(async (overrides?: Parameters<typeof saveSession>[0]) => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    await saveSession(overrides);
  }, [saveSession]);

  // ── Auto-save whenever messages change ──────────────────────────────────

  useEffect(() => {
    if (!setup || sessionLoading) return;
    debouncedSave();
  }, [messages]);

  const saveSessionRef = useRef(saveSession);
  useEffect(() => { saveSessionRef.current = saveSession; }, [saveSession]);

  useEffect(() => {
    return () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        saveTimer.current = null;
        saveSessionRef.current();
      }
    };
  }, []);

  // ── Load session on mount ────────────────────────────────────────────────

  useEffect(() => {
    if (!loadSessionId) return;
    if (loadSessionId === sessionId) return; // already active internally, no reload needed
    // Reset all state and flag as loading — prevents DataSimIntake from auto-starting interview
    setSessionLoading(true);
    setSetup(null);
    setMessages([]);
    setManifest(null);
    setConstraintConfig(DEFAULT_CONSTRAINTS);
    setAiConfig(null);
    setSimResults(null);
    setScreen('setup');
    setMaxStep(0);
    setSessionId(null);
    (async () => {
      try {
        const res = await api.get(`/api/datasim/sessions/${loadSessionId}`);
        const s = res.data.session;
        setSetup(s.setup || null);
        setMessages(s.messages || []);
        setManifest(s.manifest || null);
        setConstraintConfig(s.constraint_config || DEFAULT_CONSTRAINTS);
        setAiConfig(s.ai_config || null);
        setSimResults(s.sim_results || null);
        setSessionId(s.id);
        const restoredScreen = s.current_screen || (s.sim_results ? 'results' : s.manifest ? 'constraints' : s.setup ? 'intake' : 'setup');
        setScreen(restoredScreen);
        const restoredIdx = STEPS.findIndex(st => st.id === restoredScreen);
        setMaxStep(restoredIdx >= 0 ? restoredIdx : 0);
        toast.success('Session restored');
      } catch {
        toast.error('Failed to load session');
      } finally {
        setSessionLoading(false);
      }
    })();
  }, [loadSessionId]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  function handleSetupComplete(s: ProjectSetup) {
    setSetup(s);
    setMessages([]);
    setManifest(null);
    setSessionId(null);
    setScreen('intake');
    setMaxStep(prev => Math.max(prev, 1));
    onSessionChange?.(null);
    debouncedSave({ screen: 'intake', setup: s, messages: [], manifest: null });
  }

  async function handleRunSimulation() {
    if (!manifest?.variables?.length) {
      toast.error('No variables found. Complete the intake interview first.');
      return;
    }
    setSimulating(true);
    try {
      const manifestWithConstraints: Manifest = {
        ...manifest,
        constraint_config: {
          ...(manifest.constraint_config || {}),
          planning_fallacy: constraintConfig.planning_fallacy.enabled
            ? { inflation_factor: constraintConfig.planning_fallacy.inflation_factor }
            : undefined,
          scope_creep_factor: constraintConfig.scope_creep.enabled
            ? { inflation_pct: constraintConfig.scope_creep.inflation_pct }
            : undefined,
          parkinson: constraintConfig.parkinson.enabled ? { enabled: true } : undefined,
          student_syndrome: constraintConfig.student_syndrome.enabled ? { enabled: true } : undefined,
          external_deps: constraintConfig.external_deps.enabled ? { enabled: true } : undefined,
          key_person: constraintConfig.key_person.enabled ? { enabled: true } : undefined,
        },
      };

      const res = await api.post('/api/datasim/simulate', {
        manifest: manifestWithConstraints,
        iterations: 10000,
        ai_scenario: aiConfig || undefined,
      });
      setSimResults(res.data.results);
      setScreen('results');
      setMaxStep(4);
      toast.success('Simulation complete!');
      saveSession({ screen: 'results', simResults: res.data.results });
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Simulation failed');
    } finally {
      setSimulating(false);
    }
  }

  function handleReset() {
    setScreen('setup');
    setMaxStep(0);
    setSetup(null);
    setMessages([]);
    setManifest(null);
    setConstraintConfig(DEFAULT_CONSTRAINTS);
    setAiConfig(null);
    setSimResults(null);
    setSessionId(null);
    onSessionChange?.(null);
  }

  const completeVars = (manifest?.variables || []).filter(
    v => v.distribution?.params?.optimistic != null &&
         v.distribution?.params?.pessimistic != null &&
         v.distribution?.params?.most_likely != null
  );

  const currentStepIdx = STEPS.findIndex(s => s.id === screen);
  const effectiveMax = Math.max(maxStep, currentStepIdx);
  const unit = manifest?.primary_output_metric?.unit || setup?.metricUnit || 'weeks';

  return (
    <div className="space-y-6">

      {/* ── Active Mode Banner ────────────────────────────────────────── */}
      <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-red-100 bg-gradient-to-r from-red-50 to-rose-50">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#ef444420' }}>
          <Shield style={{ color: '#ef4444', width: 18, height: 18 }} />
        </div>
        <div>
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Active Mode</p>
          <p className="text-sm font-bold text-gray-900">DataSim Risk Analyzer</p>
        </div>
        <div className="ml-auto flex items-center gap-2 text-xs text-gray-400">
          <Activity className="w-3.5 h-3.5" />
          Monte Carlo · Multi-team · AI Impact
        </div>
      </div>

      {/* ── Step progress indicator ───────────────────────────────────── */}
      {screen !== 'setup' && (
        <div className="bg-white border border-gray-200 rounded-xl px-5 py-3 shadow-sm">
          <div className="flex items-center gap-2">
            {STEPS.map((step, idx) => {
              const isDone = idx < effectiveMax || (idx < currentStepIdx);
              const isActive = step.id === screen;
              const isReachable = idx <= effectiveMax;
              return (
                <React.Fragment key={step.id}>
                  <button
                    onClick={() => {
                      if (isReachable && !isActive) {
                        flushSave({ screen: step.id });
                        setScreen(step.id);
                      }
                    }}
                    disabled={!isReachable}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      isActive
                        ? 'bg-red-600 text-white shadow-sm'
                        : isDone
                        ? 'bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 cursor-pointer'
                        : 'bg-gray-50 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {isDone ? (
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    ) : (
                      <Circle className="w-3.5 h-3.5" />
                    )}
                    {step.label}
                  </button>
                  {idx < STEPS.length - 1 && (
                    <div className={`flex-1 h-px transition-colors ${isDone ? 'bg-green-300' : 'bg-gray-200'}`} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Screen renderer ───────────────────────────────────────────── */}

      {sessionLoading && (
        <div className="flex items-center justify-center py-16 text-gray-400 text-sm gap-2">
          <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
          Restoring session...
        </div>
      )}

      {!sessionLoading && screen === 'setup' && (
        <DataSimSetup onComplete={handleSetupComplete} />
      )}

      {!sessionLoading && screen === 'intake' && setup && (
        <DataSimIntake
          setup={setup}
          messages={messages}
          manifest={manifest}
          onMessagesUpdate={setMessages}
          onManifestUpdate={setManifest}
          onProceed={() => { flushSave({ screen: 'constraints' }); setScreen('constraints'); setMaxStep(prev => Math.max(prev, 2)); }}
        />
      )}

      {!sessionLoading && screen === 'constraints' && (
        <DataSimConstraintReview
          manifest={manifest}
          config={constraintConfig}
          onChange={setConstraintConfig}
          onRunSimulation={() => { flushSave({ screen: 'scenarios' }); setScreen('scenarios'); setMaxStep(prev => Math.max(prev, 3)); }}
          simulating={false}
          completeVarCount={completeVars.length}
        />
      )}

      {!sessionLoading && screen === 'scenarios' && (
        <DataSimScenarioBuilder
          manifest={manifest}
          aiConfig={aiConfig}
          onAIConfigChange={setAiConfig}
          onRunSimulation={handleRunSimulation}
          simulating={simulating}
          completeVarCount={completeVars.length}
        />
      )}

      {!sessionLoading && screen === 'results' && simResults && (
        <DataSimResults
          results={simResults}
          manifest={manifest}
          constraintConfig={constraintConfig}
          aiConfig={aiConfig}
          unit={unit}
          projectStartDate={setup?.projectStartDate}
          messages={messages}
          onReset={handleReset}
        />
      )}
    </div>
  );
}
